// O dinheiro da viagem com o recibo provisório dentro — a régua de meta (dona do
// total no V3) e o portão do itinerário provisório leem DESTA régua única. Saiu
// do `TripPanel` quando o total migrou do rodapé da lateral para o popover da
// régua (Painel V3): dois consumidores, um arquivo.
import type { CheckoutItem } from '@voll-intelligence/genui-contract';
import type { BookingReceipt } from './receipt_types.js';
import { receiptTotal } from './receipt.js';
import type { ModalKind, TripCost } from './derive.js';

/** O modal do PRIMEIRO item do carrinho — mesmo gate do `receiptTotal`/`CheckoutDrawer`
 *  do kit: o carrinho é homogêneo por checkout, e o `productType` do primeiro item decide
 *  para todos. `null` só quando o recibo não tem item nenhum (não deveria acontecer na
 *  prática — guarda o tipo em vez de assumir). */
const PRODUCT_MODAL: Record<CheckoutItem['productType'], ModalKind> = {
  flight: 'FLIGHT',
  hotel: 'HOTEL',
  bus: 'BUS',
  rental: 'RENTAL',
  mobility: 'MOBILITY',
};
export function receiptModal(receipt: BookingReceipt): ModalKind | null {
  const first = receipt.items[0];
  return first ? PRODUCT_MODAL[first.productType] : null;
}

/** TODOS os modais distintos do recibo. O carrinho é homogêneo no checkout de
 *  hoje (aí isto colapsa no `receiptModal`), mas o tipo não garante isso — quem
 *  pergunta "o que este recibo já reservou?" (`presentModals`, #2186) deriva de
 *  todos os itens, não do primeiro. */
export function receiptModals(receipt: BookingReceipt): Set<ModalKind> {
  return new Set(receipt.items.map((item) => PRODUCT_MODAL[item.productType]));
}

/** O total do RECIBO no formato do total da viagem. Sem voucher `tripCost` devolve null,
 *  e a régua mostraria valores por trecho e nenhum total.
 *
 *  `receiptTotal` JÁ é o total — por assento × viajantes SÓ no voo, a régua que subiu para
 *  `ui/booking/receipt.ts` para não existir em dois lugares (foi assim que um card
 *  chegou a dizer 571,42 com o checkout cobrando 563,19). Multiplicar de novo aqui
 *  cobraria o dobro.
 *
 *  A linha leva o modal do PRÓPRIO recibo (Task 9) — não mais cravada em `FLIGHT`: a
 *  bancada determinística também reserva hotel agora, e uma linha "FLIGHT" para um
 *  recibo de hotel mentiria no detalhamento do custo. */
export function receiptCost(receipt: BookingReceipt | null | undefined): TripCost | null {
  if (!receipt) return null;
  const total = receiptTotal(receipt.items, receipt.travelers);
  const modal = receiptModal(receipt);
  if (!total || !modal) return null;
  return {
    currency: total.currency,
    lines: [{ modal, amount: total.amount }],
    total: total.amount,
  };
}

/** O total da viagem COM o serviço provisório do recibo dentro dele.
 *
 *  `tripCost` só soma serviço que tem valor, então numa viagem que já trazia hotel
 *  emitido ele afirma um total sem o voo que a pessoa acabou de comprar — e esse número
 *  fica na mesma tela que o bloco que mostra o preço desse voo.
 *
 *  Precedência do documento: onde já existe linha do MESMO modal do recibo no voucher, o
 *  recibo não entra — o documento venceu a corrida. Antes da Task 9 isto olhava só
 *  `FLIGHT` cravado, e por isso uma trip cross-modal (voo já emitido + hotel provisório)
 *  perdia o provisório do hotel: a linha FLIGHT persistida "bloqueava" um recibo que nem
 *  falava de voo. Moeda diferente também o deixa de fora, pela mesma razão que faz
 *  `tripCost` devolver null nesse caso — somar BRL com USD daria um número falso. A linha
 *  do recibo entra na FRENTE das persistidas, mesmo lugar que a linha FLIGHT sempre
 *  ocupou aqui. */
export function withProvisionalService(
  persisted: TripCost | null,
  receipt: TripCost | null,
): TripCost | null {
  if (!receipt) return persisted;
  if (!persisted) return receipt;
  if (persisted.currency !== receipt.currency) return persisted;
  const receiptLineModal = receipt.lines[0]?.modal;
  if (receiptLineModal && persisted.lines.some((line) => line.modal === receiptLineModal)) {
    return persisted;
  }
  return {
    currency: persisted.currency,
    lines: [...receipt.lines, ...persisted.lines],
    total: persisted.total + receipt.total,
  };
}
